import type { AuthenticatedUser } from '@ofmos/shared/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppClient } from '../../data/ClientProvider';
import { queryKeys } from '../../data/queryKeys';

/**
 * WHO is signed in, as one cache entry.
 *
 * The session is server state (a cookie the browser cannot read), so it lives in TanStack
 * Query like every other server fact - not in a Jotai atom mirroring it (INV-13). That is
 * also what makes expiry work: the HTTP client reports a 401 by writing `null` here, and
 * the guard below redirects on the next render, wherever in the app that happens.
 */
export function useSession() {
  const client = useAppClient();
  return useQuery<AuthenticatedUser | null>({
    queryKey: queryKeys.session(),
    queryFn: () => client.auth.me(),
    // Long: the cookie outlives a page view by two weeks, and a poll per screen would be
    // one request per navigation for an answer that almost never changes.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useLogin() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      client.auth.login(email, password),
    onSuccess: (user) => {
      /**
       * Clear FIRST, then write the session: a new session may see a different data model
       * and different records than whatever was cached before it (another person, or this
       * one after a long absence), and deciding what is still valid is harder to get right
       * than throwing it away.
       */
      queryClient.clear();
      queryClient.setQueryData(queryKeys.session(), user);
    },
  });
}

/** Bootstrap: creates the admin on an instance that has no accounts, then signs in. */
export function useRegister() {
  const client = useAppClient();
  const login = useLogin();
  return useMutation({
    mutationFn: async (input: { email: string; password: string; name: string }) => {
      const user = await client.auth.register(input);
      // Registration deliberately does not issue a session (routes/auth.ts), so the
      // very next step is a login with the credentials we just used.
      await login.mutateAsync({ email: input.email, password: input.password });
      return user;
    },
  });
}

export function useLogout() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.auth.logout(),
    onSettled: () => {
      // Even a failed logout ends the session in this tab: the alternative is a UI that
      // claims to be signed in with a cookie the server may already have dropped.
      queryClient.clear();
      queryClient.setQueryData(queryKeys.session(), null);
    },
  });
}
